// Citation grouping + labeling for the forecast report UI.
//
// The forecast LLM frequently attaches one source to several points, so a
// reason's `citations` list repeats the same `source_url` with different
// quotes. Rendered as bare domain links that read as `[Reuters, Reuters]`
// noise. We don't mutate the stored report (it must stay a faithful trace) —
// these helpers reshape citations at render time, deduping on the field the
// user actually sees: the domain label in reasons, the quote in resolution
// criteria.

// `superscript` lives in its own module so this file can import from sourceLabel.js without a
// cycle (citations → sourceLabel → superscript); it is re-exported here for its existing readers.
import { sourceNameOf } from "./report/sourceLabel.js";
import { superscript } from "./report/superscript.js";

export { superscript } from "./report/superscript.js";

// A short publication label for a source URL: the site's name, first letter
// capital (e.g. "https://uk.reuters.com/x" -> "Reuters",
// "https://news.bbc.co.uk/y" -> "Bbc"; a URL with no site name, such as
// "http://10.0.0.1/x", reads as its host). Non-URL strings (a bare label the
// model emitted instead of a link) and falsy input pass through unchanged.
export function srcLabel(s) {
  if (!s || !/^https?:\/\//.test(s)) return s;
  try {
    return sourceNameOf(s) || s;
  } catch (_) {
    return s;
  }
}

// Group ONE reason's citations into one entry per distinct `source_url`, in
// first-seen order. Each entry carries that URL's distinct quotes (exact
// duplicate quotes are dropped, so an exact (url, quote) pair collapses) —
// surfaced on hover, so no quote text is lost. Labeling is NOT done here: it's
// report-wide (see buildCitationLabels) so a source's footnote is stable across
// Why Yes / Why No / Sources.
//
// Returns: [{ source_url, quotes: string[] }].
export function groupCitationsByUrl(citations) {
  const cits = citations || [];
  const order = [];
  const quotesByUrl = new Map();
  for (const c of cits) {
    const url = c.source_url;
    if (!quotesByUrl.has(url)) {
      order.push(url);
      quotesByUrl.set(url, []);
    }
    const quotes = quotesByUrl.get(url);
    if (c.quote && !quotes.includes(c.quote)) quotes.push(c.quote);
  }
  return order.map((url) => ({ source_url: url, quotes: quotesByUrl.get(url) }));
}

// Build a report-wide map of source_url -> display label, so a given source's
// footnote is identical everywhere it appears (Why Yes, Why No, Sources). All
// distinct URLs are collected in first-seen order across the report, grouped by
// their domain label; when a domain has several distinct URLs they are numbered
// "Reuters¹", "Reuters²", … (footnote-style, stable), and a lone URL stays bare
// ("Bbc"). `extraUrls` folds in URLs shown only in the Sources list (e.g. the
// contract-sources fallback) so they share the same footnotes. Returns a Map.
export function buildCitationLabels(report, extraUrls = []) {
  const urlsByLabel = new Map();
  for (const url of reportUrlsInOrder(report, extraUrls)) {
    const base = srcLabel(url);
    if (!urlsByLabel.has(base)) urlsByLabel.set(base, []);
    urlsByLabel.get(base).push(url);
  }

  const labels = new Map();
  for (const [base, urls] of urlsByLabel) {
    if (urls.length === 1) {
      labels.set(urls[0], base);
    } else {
      urls.forEach((url, i) => labels.set(url, base + superscript(i + 1)));
    }
  }
  return labels;
}

/**
 * Report-wide reference ids: source_url -> its 0-based index among the report's distinct
 * http(s) URLs in first-seen order (the order `buildCitationLabels` numbers them in). The
 * telemetry key of a reference link, so one source keeps one id wherever the report shows it.
 */
export function buildReferenceIndex(report, extraUrls = []) {
  return new Map(reportUrlsInOrder(report, extraUrls).map((url, i) => [url, i]));
}

/** The report's distinct http(s) URLs in first-seen order, then `extraUrls`. */
function reportUrlsInOrder(report, extraUrls) {
  const order = [];
  const seen = new Set();
  // Only real http(s) URLs join the footnote map. Non-URL source strings (e.g.
  // a bare "CNBC" in contract sources) render verbatim elsewhere and must not
  // share a domain base with a real URL — otherwise a lone URL would be
  // wrongly numbered (CNBC¹) just because a non-URL "CNBC" coexists.
  const add = (url) => {
    if (url && /^https?:\/\//.test(url) && !seen.has(url)) {
      seen.add(url);
      order.push(url);
    }
  };
  const fromList = (list) => {
    for (const item of list || []) for (const c of item.citations || []) add(c.source_url);
  };
  fromList(report?.reasons_for);
  fromList(report?.reasons_against);
  fromList(report?.summaries);
  fromList(report?.resolution_criteria_analysis?.criteria);
  for (const url of report?.sources || []) add(url);
  for (const url of extraUrls) add(url);
  return order;
}

// Resolve ONE reason's citations into labeled chips for display: group by URL
// (within the reason), then attach each source's report-wide footnote from
// `labels` (see buildCitationLabels), falling back to a bare domain label. The
// single place the "group + label" rule lives, so every render surface
// (Why Yes / Why No / export) stays consistent without repeating it.
export function citationChips(citations, labels) {
  const lookup = labels || new Map();
  return groupCitationsByUrl(citations).map((g) => ({
    ...g,
    label: lookup.get(g.source_url) || srcLabel(g.source_url),
  }));
}

// Keep the first citation per distinct quote, preserving order. Used by the
// resolution-criteria section, where the quote (not the domain) is the visible
// text, so a repeated quote — regardless of URL — is what reads as duplicated.
export function dedupeCitationsByQuote(citations) {
  const cits = citations || [];
  const seen = new Set();
  const out = [];
  for (const c of cits) {
    if (seen.has(c.quote)) continue;
    seen.add(c.quote);
    out.push(c);
  }
  return out;
}
